/**
 * Group Form Modal – create/edit nested prompt groups (text + sender)
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { X } from 'lucide-react';

import { Button, Input, LoadingSpinner } from '@/components/ui';

import { descendantIdsIncludingSelf } from '../../../utils/group-tree.utils';

import type {
  PromptGroup,
  CreateGroupPayload,
  TextPromptGroup,
  SenderPromptGroup,
} from '../../../types/campaign-texts.types';

import { ParentFolderFields } from './ParentFolderFields';

interface GroupFormModalProps {
  open: boolean;
  group?: PromptGroup | null;
  mode?: 'text' | 'sender';
  allTextGroups?: TextPromptGroup[];
  allSenderGroups?: SenderPromptGroup[];
  isPending: boolean;
  onClose: () => void;
  onSubmit: (data: CreateGroupPayload) => void;
}

export function GroupFormModal({
  open,
  group,
  mode = 'text',
  allTextGroups = [],
  allSenderGroups = [],
  isPending,
  onClose,
  onSubmit,
}: GroupFormModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [prompt, setPrompt] = useState('');
  const [parentId, setParentId] = useState<string | null>(null);

  const treeForParentSelect = useMemo(() => {
    if (mode === 'text') return allTextGroups;
    if (mode === 'sender') return allSenderGroups;
    return [];
  }, [mode, allTextGroups, allSenderGroups]);

  const excludedParentIds = useMemo(() => {
    if (!group) return new Set<string>();
    if (mode === 'text') return descendantIdsIncludingSelf(group.id, allTextGroups);
    if (mode === 'sender') return descendantIdsIncludingSelf(group.id, allSenderGroups);
    return new Set<string>();
  }, [mode, group, allTextGroups, allSenderGroups]);

  const parentOptions = useMemo(
    () => treeForParentSelect.filter((g) => !excludedParentIds.has(g.id)),
    [treeForParentSelect, excludedParentIds],
  );

  useEffect(() => {
    if (!open) return;
    setName(group?.name ?? '');
    setDescription(group?.description ?? '');
    setPrompt(group?.prompt ?? '');
    setParentId(group?.parent_id ?? null);
  }, [open, group]);

  const handleSubmit = useCallback(() => {
    if (!name.trim()) return;
    const payload: CreateGroupPayload = {
      name: name.trim(),
      description: description.trim() || undefined,
      prompt: prompt.trim() || undefined,
    };
    if (mode === 'text' || mode === 'sender') payload.parentId = parentId;
    onSubmit(payload);
  }, [name, description, prompt, parentId, mode, onSubmit]);

  const canSubmit = name.trim() && !isPending;
  const showParentSelect = mode === 'text' || mode === 'sender';

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-xl border bg-card p-6 shadow-xl space-y-4 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">{group ? 'Edit Group' : 'New Group'}</h2>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Clicker Texts" maxLength={100} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Description</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" maxLength={500} />
          </div>
          {showParentSelect && (
            <ParentFolderFields
              parentId={parentId}
              onParentChange={setParentId}
              parentOptions={parentOptions}
              treeForLabels={treeForParentSelect}
            />
          )}
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              Prompt rules (optional for folders)
            </label>
            <textarea
              className="w-full min-h-[120px] rounded-md border border-border bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40 resize-y"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Write your prompt rules for this group..."
              maxLength={10000}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={!canSubmit}>
            {isPending && <LoadingSpinner className="h-3 w-3 mr-1.5" />}
            {group ? 'Save Changes' : 'Create Group'}
          </Button>
        </div>
      </div>
    </div>
  );
}
